<style lang="scss">
    .error {
        color: red;
    }
    .success {
        color: green;
    }
    form.success {
        display: none;
    }
    .eqs-cms-irtool {
        :global(.loader) {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            display: grid;
            place-items: center;
            z-index: 9999;
            width: 100%; /* Full width (cover the whole page) */
            height: 100%; /* Full height (cover the whole page) */
            background-color: rgba(
                0,
                0,
                0,
                0.5
            ); /* Black background with opacity */
            cursor: pointer; /* Add a pointer on hover */
        }
        :global {
            .eqs-form-feild {
                margin-bottom: 20px;
            }
        }
    }
</style>

<script lang="ts">
    import { ContactApiPostData, createSubscription } from './utils/api';

    import PersonInfoFormComponent from './components/PersonInfoForm.svelte';
    import type { PersonInfoType, GoogleRecaptchaConfig } from './utils/types';
    import { googleRecaptchaConfig } from './utils/constants';
    import { _ } from 'svelte-i18n';
    import RecaptchaSubscribeForm from './components/RecaptchaSubscribeForm.svelte';
    import { RECAPTCHA_TARGET_HTML_ID } from '../../config';
    import { prepareKeyValuePairs } from './utils/ops';
    import type { ValidateFunction } from '@eqs/cms-svelte-irtools/form';
    import { combineValidates } from '@eqs/cms-svelte-irtools/form';

    type ContactFormType = {
        distributionList: string[];
        supportedDist: string[];
        personalInfo: PersonInfoType;
    };

    let error = '',
        success = '',
        goBackUrl = '',
        scrollToTop = '',
        isPageLoaded = true;

    const formObject: ContactFormType = {
        distributionList: ['website-subscribers'],
        supportedDist: [],
        personalInfo: {
            email: ''
        }
    };

    /**
     * Render Subscribe form Captcha
     *
     */
    let subscribeRecaptcha: number;
    const onloadIrSubscribeRecaptcha = () => {
        const googleRecaptcha = window.grecaptcha;
        subscribeRecaptcha = googleRecaptcha.render(RECAPTCHA_TARGET_HTML_ID, {
            sitekey: googleRecaptchaConfig.sitekey
        } as GoogleRecaptchaConfig);
    };

    const formatFormData = (
        data: ContactFormType & { subscribeRecaptchaToken: string }
    ): ContactApiPostData => {
        const metaFieldsMap = {
            age: 'Age',
            ageGroup: 'Age Group'
        };
        const otherEmailsFieldsMap = {
            emailReception: 'Reception'
        };
        const otherPhonesFieldsMap = {
            mobilePhoneBusiness: 'Mobile Business',
            mobilePhonePrivate: 'Mobile Private',
            phoneReception: 'Reception'
        };
        const faxesFields = {
            faxReception: 'Reception'
        };
        console.log(data.personalInfo.affiliation);
        return {
            ...data,
            ...data.personalInfo,
            email: data.personalInfo.email || '',
            meta: prepareKeyValuePairs(
                data.personalInfo,
                metaFieldsMap,
                'name'
            ),
            otherEmails: prepareKeyValuePairs(
                data.personalInfo,
                otherEmailsFieldsMap,
                'category'
            ),
            otherPhones: prepareKeyValuePairs(
                data.personalInfo,
                otherPhonesFieldsMap,
                'category'
            ),
            faxes: prepareKeyValuePairs(
                data.personalInfo,
                faxesFields,
                'category'
            ),
            category: data.personalInfo.affiliation
                ? [data.personalInfo.affiliation]
                : ['website-subscribers'],

            otherLocales: data.personalInfo.otherLocales
                ? [data.personalInfo.otherLocales]
                : []
        };
    };

    const validates: { [key: string]: ValidateFunction } = {};
    export const validate: ValidateFunction = async () => {
        await combineValidates(...Object.values(validates))();
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            await validate();
            isPageLoaded = false;
            try {
                error = ''; // reset the error
                const googleRecaptcha = window.grecaptcha;
                const token = await googleRecaptcha.execute(
                    subscribeRecaptcha,
                    {
                        action: 'subscribeForm'
                    }
                );
                const formattedData = formatFormData({
                    ...formObject,
                    subscribeRecaptchaToken: token
                });
                await createSubscription(formattedData);
                isPageLoaded = true;
                success = $_('contact_form.successfully_subscribed');
            } catch (e: unknown) {
                const errorObject = e as Error;
                success = '';
                isPageLoaded = true;
                error = `${
                    errorObject.message || $_('generic.errors.unknown')
                }`;
            }
            scrollToTop.scrollIntoView();
        } catch (e) {}
    };
</script>

<svelte:head>
    <script src="https://www.google.com/recaptcha/api.js" async defer></script>
</svelte:head>

<svelte:window on:load="{onloadIrSubscribeRecaptcha}" />

<div class="eqs-cms-irtool" bind:this="{scrollToTop}">
    <!-- <h1>{$_('contact_form.heading')}</h1> -->
    {#if error}
        <span class="error">{error}</span>
    {/if}
    {#if success}
        <span class="success">{success}</span>
        <br />
        <a href="{goBackUrl}" class="goBackButton"
            >{$_('contact_form.buttons.back_button')}</a
        >
    {/if}
    {#if !isPageLoaded}
        <div class="loader">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                style="margin: auto; background: transparent; display: block; shape-rendering: auto;"
                width="150px"
                height="150px"
                viewBox="0 0 100 100"
                preserveAspectRatio="xMidYMid"
            >
                <circle
                    cx="50"
                    cy="50"
                    fill="none"
                    stroke="#b49963"
                    stroke-width="10"
                    r="35"
                    stroke-dasharray="164.93361431346415 56.97787143782138"
                >
                    <animateTransform
                        attributeName="transform"
                        type="rotate"
                        repeatCount="indefinite"
                        dur="1s"
                        values="0 50 50;360 50 50"
                        keyTimes="0;1"></animateTransform>
                </circle>
            </svg>
        </div>
    {/if}
    <form
        on:submit|preventDefault="{onSubmit}"
        id="subscribeForm"
        class:success
    >
        <section>
            <PersonInfoFormComponent
                bind:data="{formObject.personalInfo}"
                bind:distributionList="{formObject.distributionList}"
                bind:validate="{validates.personalInfo}"
                bind:supportedDist="{formObject.supportedDist}"
            />
        </section>
        <section>
            <RecaptchaSubscribeForm
                googleRecaptchaConfig="{googleRecaptchaConfig}"
            />
        </section>
        <button type="submit" on:click="{onSubmit}" class="buttonBox">
            {$_('contact_form.buttons.submit')}
        </button>
    </form>
</div>
